// ============================================================================
// DRAW SEARCH — Classic textbook visualization
// Array shown as horizontal cells (table-like). L/R pointers below cells,
// mid marker as vertical line, eliminated range dimmed, target displayed
// prominently. Works for all 6 search algorithms.
// ============================================================================

import { type ExecutionStep, StepType } from '../../../../core/types/rust-engine-interfaces';

const COLORS = {
  cellBg: 'rgba(30, 41, 59, 0.85)',
  cellBgInRange: 'rgba(59, 130, 246, 0.25)',
  cellBgEliminated: 'rgba(15, 23, 42, 0.6)',
  cellBorder: 'rgba(71, 85, 105, 0.5)',
  cellBorderInRange: '#3b82f6',
  cellBorderMid: '#f59e0b',
  cellBorderFound: '#22c55e',
  text: '#e2e8f0',
  textEliminated: '#475569',
  textMid: '#f59e0b',
  textFound: '#22c55e',
  pointerLeft: '#3b82f6',
  pointerRight: '#a855f7',
  pointerMid: '#f59e0b',
  targetBg: 'rgba(245, 158, 11, 0.15)',
  targetBorder: '#f59e0b',
  targetText: '#fbbf24',
  rangeLabel: '#94a3b8',
  eliminatedLine: 'rgba(100, 116, 139, 0.4)',
};

const LAYOUT = {
  padX: 24,
  padTop: 60,
  cellH: 44,
  cellMinW: 32,
  cellMaxW: 60,
  cellGap: 3,
  fontSize: 14,
  fontSizeSmall: 10,
  pointerH: 20,
  pointerGap: 4,
  targetBoxH: 28,
};

type SearchKind = 'binary' | 'jump' | 'interpolation' | 'exponential' | 'ternary' | 'fibonacci';

function detectSearchKind(algorithmId: string): SearchKind | null {
  const id = algorithmId.toLowerCase();
  if (id.includes('binary-search')) return 'binary';
  if (id.includes('jump-search')) return 'jump';
  if (id.includes('interpolation')) return 'interpolation';
  if (id.includes('exponential')) return 'exponential';
  if (id.includes('ternary')) return 'ternary';
  if (id.includes('fibonacci-search')) return 'fibonacci';
  return null;
}

export function isSearchAlgorithm(algorithmId: string): boolean {
  return detectSearchKind(algorithmId) !== null;
}

interface SearchRange {
  left: number;
  right: number;
  mid: number[];
  found: number[];
  eliminated: number[];
}

function parseSearchRange(step: ExecutionStep, n: number): SearchRange {
  const meta = step.payload.type === 'array' ? step.payload.metadata : {};
  const expl = step.metadata?.explanation ?? '';

  // Parse range from explanation: [L..R] or range [L..R]
  const rangeMatch = expl.match(/\[\s*(\d+)\s*\.\.\s*(\d+)\s*\]/);
  let left = rangeMatch ? parseInt(rangeMatch[1]!, 10) : 0;
  let right = rangeMatch ? parseInt(rangeMatch[2]!, 10) : n - 1;

  // Also check "left=X" / "right=Y" in explanation
  const leftMatch = expl.match(/(?:lo|left)\s*=\s*(\d+)/i);
  const rightMatch = expl.match(/(?:hi|right)\s*=\s*(\d+)/i);
  if (leftMatch) left = parseInt(leftMatch[1]!, 10);
  if (rightMatch) right = parseInt(rightMatch[1]!, 10);

  // Clamp
  left = Math.max(0, Math.min(left, n - 1));
  right = Math.max(0, Math.min(right, n - 1));
  if (left > right) { const t = left; left = right; right = t; }

  // Mid points: from compareIndices, currentIndex, or explanation
  const mid: number[] = [];
  if (meta.compareIndices) {
    for (const idx of meta.compareIndices) {
      if (typeof idx === 'number' && idx >= 0 && idx < n) mid.push(idx);
    }
  }
  if (meta.currentIndex !== undefined && meta.currentIndex >= 0 && meta.currentIndex < n) {
    if (!mid.includes(meta.currentIndex)) mid.push(meta.currentIndex);
  }
  // Parse mid1/mid2 from explanation (ternary search)
  const mid1Match = expl.match(/mid1\s*=\s*(\d+)/i);
  const mid2Match = expl.match(/mid2\s*=\s*(\d+)/i);
  if (mid1Match) { const m = parseInt(mid1Match[1]!, 10); if (m >= 0 && m < n && !mid.includes(m)) mid.push(m); }
  if (mid2Match) { const m = parseInt(mid2Match[1]!, 10); if (m >= 0 && m < n && !mid.includes(m)) mid.push(m); }
  // Parse mid=N from explanation
  const midMatch = expl.match(/mid\s*=\s*(\d+)/i);
  if (midMatch) { const m = parseInt(midMatch[1]!, 10); if (m >= 0 && m < n && !mid.includes(m)) mid.push(m); }

  // Found indices
  const found: number[] = meta.sortedIndices ? meta.sortedIndices.filter(i => typeof i === 'number' && i >= 0 && i < n) : [];

  // Eliminated: everything outside [left, right] that's not found
  const eliminated: number[] = [];
  for (let i = 0; i < n; i++) {
    if (i < left || i > right) {
      if (!found.includes(i)) eliminated.push(i);
    }
  }

  // For jump search: highlightIndices is the current block, not [L..R]
  // If highlightIndices is present and smaller than full range, use it as in-range
  const highlight = meta.highlightIndices ?? [];
  if (highlight.length > 0 && highlight.length < (right - left + 1)) {
    // highlightIndices is the active subset — eliminate everything else
    for (let i = left; i <= right; i++) {
      if (!highlight.includes(i) && !found.includes(i)) {
        eliminated.push(i);
      }
    }
  }

  return { left, right, mid, found, eliminated };
}

function parseTarget(expl: string): number | null {
  const m = expl.match(/target\s*=\s*(-?\d+)/i);
  return m ? parseInt(m[1]!, 10) : null;
}

export function drawSearch(
  ctx: CanvasRenderingContext2D,
  currentStep: ExecutionStep,
  W: number,
  H: number,
  algorithmId: string,
): void {
  if (currentStep.payload.type !== 'array') return;
  const items = currentStep.payload.items;
  const values = items.map(i => Number(i.value) || 0);
  const n = values.length;
  if (n === 0) return;

  const kind = detectSearchKind(algorithmId);
  if (!kind) return;

  const range = parseSearchRange(currentStep, n);
  const target = parseTarget(currentStep.metadata?.explanation ?? '');
  const isComplete = currentStep.stepType === StepType.Completion;
  const isInit = currentStep.stepType === StepType.Initialization;

  // Layout
  const isNarrow = W < 480;
  const padX = isNarrow ? 12 : LAYOUT.padX;
  const padTop = LAYOUT.padTop;
  const availW = W - padX * 2;
  const cellGap = LAYOUT.cellGap;
  const cellW = Math.max(LAYOUT.cellMinW, Math.min(LAYOUT.cellMaxW, (availW - (n - 1) * cellGap) / n));
  const totalW = n * cellW + (n - 1) * cellGap;
  const startX = padX + Math.max(0, (availW - totalW) / 2);
  const cellY = padTop;
  const cellH = LAYOUT.cellH;

  // ── Target box (top center) ──────────────────────────────────────────────
  if (target !== null) {
    const label = `Target: ${target}`;
    ctx.font = `bold ${isNarrow ? 12 : 14}px monospace`;
    const tw = ctx.measureText(label).width;
    const boxW = tw + 24;
    const boxH = LAYOUT.targetBoxH;
    const boxX = (W - boxW) / 2;
    const boxY = 12;

    ctx.fillStyle = COLORS.targetBg;
    ctx.strokeStyle = COLORS.targetBorder;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxW, boxH, 6);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = COLORS.targetText;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, W / 2, boxY + boxH / 2);
  }

  // ── Draw cells ───────────────────────────────────────────────────────────
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${isNarrow ? 12 : LAYOUT.fontSize}px monospace`;

  for (let i = 0; i < n; i++) {
    const x = startX + i * (cellW + cellGap);
    const isInRange = i >= range.left && i <= range.right && !range.eliminated.includes(i);
    const isEliminated = range.eliminated.includes(i);
    const isMid = range.mid.includes(i);
    const isFound = range.found.includes(i);

    // Cell background
    if (isFound) {
      ctx.fillStyle = 'rgba(34, 197, 94, 0.25)';
    } else if (isMid) {
      ctx.fillStyle = 'rgba(245, 158, 11, 0.2)';
    } else if (isInRange) {
      ctx.fillStyle = COLORS.cellBgInRange;
    } else if (isEliminated) {
      ctx.fillStyle = COLORS.cellBgEliminated;
    } else {
      ctx.fillStyle = COLORS.cellBg;
    }

    // Cell border
    if (isFound) {
      ctx.strokeStyle = COLORS.cellBorderFound;
      ctx.lineWidth = 2;
    } else if (isMid) {
      ctx.strokeStyle = COLORS.cellBorderMid;
      ctx.lineWidth = 2;
    } else if (isInRange) {
      ctx.strokeStyle = COLORS.cellBorderInRange;
      ctx.lineWidth = 1.5;
    } else {
      ctx.strokeStyle = COLORS.cellBorder;
      ctx.lineWidth = 1;
    }

    ctx.beginPath();
    ctx.roundRect(x, cellY, cellW, cellH, 6);
    ctx.fill();
    ctx.stroke();

    // Value text
    if (isFound) {
      ctx.fillStyle = COLORS.textFound;
    } else if (isMid) {
      ctx.fillStyle = COLORS.textMid;
    } else if (isEliminated) {
      ctx.fillStyle = COLORS.textEliminated;
    } else {
      ctx.fillStyle = COLORS.text;
    }
    ctx.fillText(String(values[i]), x + cellW / 2, cellY + cellH / 2);

    // Index label below cell
    ctx.font = `${isNarrow ? 8 : LAYOUT.fontSizeSmall}px monospace`;
    ctx.fillStyle = isMid ? COLORS.textMid : isFound ? COLORS.textFound : COLORS.rangeLabel;
    ctx.fillText(String(i), x + cellW / 2, cellY + cellH + 10);
    ctx.font = `bold ${isNarrow ? 12 : LAYOUT.fontSize}px monospace`;

    // Strikethrough for eliminated cells
    if (isEliminated) {
      ctx.strokeStyle = COLORS.eliminatedLine;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + 4, cellY + cellH / 2);
      ctx.lineTo(x + cellW - 4, cellY + cellH / 2);
      ctx.stroke();
    }
  }

  // ── Draw L/R pointers below cells ────────────────────────────────────────
  const pointerY = cellY + cellH + 24;
  const ptrH = LAYOUT.pointerH;

  if (!isInit && !isComplete) {
    // Left pointer (L)
    if (range.left >= 0 && range.left < n) {
      const lx = startX + range.left * (cellW + cellGap) + cellW / 2;
      ctx.strokeStyle = COLORS.pointerLeft;
      ctx.fillStyle = COLORS.pointerLeft;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(lx, pointerY);
      ctx.lineTo(lx, pointerY - ptrH);
      ctx.stroke();
      // Arrowhead
      ctx.beginPath();
      ctx.moveTo(lx, pointerY);
      ctx.lineTo(lx - 4, pointerY - 6);
      ctx.lineTo(lx + 4, pointerY - 6);
      ctx.closePath();
      ctx.fill();
      // Label
      ctx.font = `bold ${isNarrow ? 9 : 10}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('L', lx, pointerY + 2);
    }

    // Right pointer (R)
    if (range.right >= 0 && range.right < n) {
      const rx = startX + range.right * (cellW + cellGap) + cellW / 2;
      ctx.strokeStyle = COLORS.pointerRight;
      ctx.fillStyle = COLORS.pointerRight;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(rx, pointerY);
      ctx.lineTo(rx, pointerY - ptrH);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(rx, pointerY);
      ctx.lineTo(rx - 4, pointerY - 6);
      ctx.lineTo(rx + 4, pointerY - 6);
      ctx.closePath();
      ctx.fill();
      ctx.font = `bold ${isNarrow ? 9 : 10}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('R', rx, pointerY + 2);
    }
  }

  // ── Draw mid marker(s) above cells ───────────────────────────────────────
  if (!isInit && !isComplete) {
    for (let mi = 0; mi < range.mid.length; mi++) {
      const midIdx = range.mid[mi]!;
      if (midIdx < 0 || midIdx >= n) continue;
      const mx = startX + midIdx * (cellW + cellGap) + cellW / 2;
      const label = range.mid.length > 1 ? (mi === 0 ? 'm1' : 'm2') : 'mid';

      // Vertical dashed line through cell
      ctx.strokeStyle = COLORS.pointerMid;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(mx, cellY - 4);
      ctx.lineTo(mx, cellY + cellH + 4);
      ctx.stroke();
      ctx.setLineDash([]);

      // Label above cell
      ctx.fillStyle = COLORS.pointerMid;
      ctx.font = `bold ${isNarrow ? 9 : 10}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(label, mx, cellY - 4);
    }
  }

  // ── Range bracket [L..R] below pointers ──────────────────────────────────
  if (!isInit && !isComplete && range.left <= range.right) {
    const sx = startX + range.left * (cellW + cellGap) - cellGap / 2;
    const ex = startX + range.right * (cellW + cellGap) + cellW + cellGap / 2;
    const bracketY = pointerY + ptrH + 8;

    ctx.strokeStyle = COLORS.rangeLabel;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(sx, bracketY + 4);
    ctx.lineTo(sx, bracketY);
    ctx.lineTo(ex, bracketY);
    ctx.lineTo(ex, bracketY + 4);
    ctx.stroke();

    ctx.fillStyle = COLORS.rangeLabel;
    ctx.font = `${isNarrow ? 8 : 9}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`search range [${range.left}..${range.right}]`, (sx + ex) / 2, bracketY + 6);
  }

  // ── Found / not found banner ─────────────────────────────────────────────
  if (isComplete) {
    const expl = currentStep.metadata?.explanation ?? '';
    const isFound = range.found.length > 0 || expl.toLowerCase().includes('found at');
    const bannerText = isFound ? expl : expl;
    ctx.font = `bold ${isNarrow ? 11 : 13}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const tw = ctx.measureText(bannerText).width;
    const bw = Math.min(W - 16, tw + 24);
    const bh = 26;
    const bx = (W - bw) / 2;
    const by = cellY + cellH + 30;

    ctx.fillStyle = isFound ? 'rgba(34, 197, 94, 0.15)' : 'rgba(248, 113, 113, 0.15)';
    ctx.strokeStyle = isFound ? COLORS.cellBorderFound : '#f87171';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, bh, 6);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = isFound ? COLORS.textFound : '#f87171';
    ctx.fillText(bannerText, W / 2, by + bh / 2);
  }

  // ── Algorithm name label (bottom-left) ───────────────────────────────────
  const algoNames: Record<SearchKind, string> = {
    binary: 'Binary Search',
    jump: 'Jump Search',
    interpolation: 'Interpolation Search',
    exponential: 'Exponential Search',
    ternary: 'Ternary Search',
    fibonacci: 'Fibonacci Search',
  };
  ctx.font = `bold ${isNarrow ? 9 : 10}px monospace`;
  ctx.fillStyle = COLORS.rangeLabel;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.fillText(algoNames[kind], padX, H - 4);
}
